import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Archive, CheckCircle, ClipboardList, RefreshCw, ShieldCheck, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const STATUS_LABELS = {
  hana_owned_free_use: "Hana — libre",
  free_with_registration: "Gratuit avec inscription",
  free_reference: "Référence gratuite",
  commercial_license_required_quote: "Licence commerciale sur devis",
  commercial_license_required_priced: "Licence commerciale tarifée",
  license_required_quote_based: "Licence sur devis",
  review_required: "À vérifier",
};

export default function QuestionnaireAvailabilityTab() {
  const [items, setItems] = useState([]);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStudies, setLoadingStudies] = useState(true);
  const [busyCode, setBusyCode] = useState(null);
  const [busyStudyId, setBusyStudyId] = useState(null);
  const [search, setSearch] = useState("");

  const fetchQuestionnaires = async () => {
    setLoading(true);
    try {
      const res = await api.get("/studies/questionnaires?includeInactive=true");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Impossible de charger le catalogue questionnaires");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudies = async () => {
    setLoadingStudies(true);
    try {
      const res = await api.get("/studies");
      setStudies(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Impossible de charger les études");
    } finally {
      setLoadingStudies(false);
    }
  };

  useEffect(() => { fetchQuestionnaires(); fetchStudies(); }, []);

  const visibleItems = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items
      .filter((q) => !s || [q.name, q.code, q.owner, q.licenseStatus, ...(q.cancerTypes || [])].join(" ").toLowerCase().includes(s))
      .sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable) || String(a.name).localeCompare(String(b.name)));
  }, [items, search]);

  const counts = useMemo(() => ({
    total: items.length,
    available: items.filter((q) => q.isAvailable).length,
    hana: items.filter((q) => q.isHanaOwned).length,
    restricted: items.filter((q) => !q.isAvailable).length,
  }), [items]);

  const toggleAvailability = async (q) => {
    const next = !q.isAvailable;
    const ok = window.confirm(next
      ? `Activer "${q.name}" ? Confirmez que Hana est en règle pour cet usage.`
      : `Désactiver "${q.name}" du catalogue disponible ?`);
    if (!ok) return;
    setBusyCode(q.code);
    try {
      const res = await api.patch(`/studies/questionnaires/${q.code}/availability`, {
        isAvailable: next,
        note: next ? "Activation manuelle après validation conformité" : "Désactivation manuelle",
      });
      setItems((prev) => prev.map((item) => item.code === q.code ? res.data : item));
      toast.success(next ? "Questionnaire activé" : "Questionnaire désactivé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de mise à jour");
    } finally {
      setBusyCode(null);
    }
  };

  const archiveStudy = async (study) => {
    const ok = window.confirm(`Archiver l'étude "${study.name}" ? Elle ne sera plus active, mais les données seront conservées.`);
    if (!ok) return;
    setBusyStudyId(study.id);
    try {
      await api.delete(`/studies/${study.id}`);
      toast.success("Étude archivée");
      await fetchStudies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'archivage");
    } finally {
      setBusyStudyId(null);
    }
  };

  const deleteStudyPermanently = async (study) => {
    const typed = window.prompt(`Suppression définitive de "${study.name}". Tapez DELETE pour confirmer.`);
    if (typed !== "DELETE") return;
    setBusyStudyId(study.id);
    try {
      await api.delete(`/studies/${study.id}/permanent?confirm=DELETE`);
      toast.success("Étude supprimée définitivement");
      await fetchStudies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression définitive");
    } finally {
      setBusyStudyId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="questionnaire-availability-tab">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800" data-testid="questionnaire-availability-title">
            <ClipboardList className="h-5 w-5 text-emerald-600" /> Catalogue questionnaires
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-testid="questionnaire-availability-subtitle">
            Tous les questionnaires restent présents, seuls les questionnaires activés apparaissent dans les flux d'enrôlement.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchQuestionnaires}
          disabled={loading}
          data-testid="questionnaire-availability-refresh-button"
          className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-[background-color,color] hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-4" data-testid="questionnaire-availability-kpis">
        <Metric label="Total" value={counts.total} testId="questionnaire-kpi-total" />
        <Metric label="Disponibles" value={counts.available} testId="questionnaire-kpi-available" tone="emerald" />
        <Metric label="Hana libres" value={counts.hana} testId="questionnaire-kpi-hana" tone="blue" />
        <Metric label="À activer manuellement" value={counts.restricted} testId="questionnaire-kpi-restricted" tone="amber" />
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="questionnaire-availability-warning">
        <div className="flex gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
          <p className="text-sm font-medium leading-relaxed text-amber-900">
            EORTC, EuroQol, FACIT/FKSI et autres instruments sous licence restent visibles ici pour inventaire, mais désactivés par défaut. Activez-les seulement après obtention des droits nécessaires.
          </p>
        </div>
      </div>

      <input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        data-testid="questionnaire-availability-search-input"
        placeholder="Rechercher par nom, code, propriétaire, licence ou pathologie..."
        className="min-h-[44px] w-full rounded-lg border border-slate-200 px-4 text-sm outline-none transition-[border-color,box-shadow] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
      />

      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="study-management-panel">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="flex items-center gap-2 font-bold text-slate-800" data-testid="study-management-title">
              <Archive className="h-4 w-4 text-slate-600" /> Études / protocoles
            </h3>
            <p className="text-sm text-slate-500" data-testid="study-management-subtitle">Archivage réversible ou suppression définitive avec confirmation.</p>
          </div>
          <button
            type="button"
            onClick={fetchStudies}
            disabled={loadingStudies}
            data-testid="study-management-refresh-button"
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loadingStudies ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>
        {loadingStudies ? (
          <div className="py-8 text-center text-sm text-slate-400" data-testid="study-management-loading">Chargement des études...</div>
        ) : studies.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400" data-testid="study-management-empty">Aucune étude</div>
        ) : (
          <div className="max-h-[360px] overflow-auto rounded-lg border border-slate-100" data-testid="study-management-list">
            {studies.map((study) => (
              <div key={study.id} className="grid gap-3 border-b border-slate-100 p-3 last:border-b-0 lg:grid-cols-[1fr_130px_220px] lg:items-center" data-testid={`study-management-row-${study.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-900" data-testid={`study-management-name-${study.id}`}>{study.name}</p>
                  <p className="mt-1 text-xs text-slate-500" data-testid={`study-management-meta-${study.id}`}>
                    {study.sponsor || "Sponsor non renseigné"} · {study.questionnaires?.length || 0} questionnaire(s) · {study.enrolledCount || 0} patient(s)
                  </p>
                </div>
                <span className={`w-fit rounded-full px-2.5 py-1 text-xs font-bold ${study.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid={`study-management-status-${study.id}`}>
                  {study.status || "—"}
                </span>
                <div className="flex flex-wrap gap-2 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => archiveStudy(study)}
                    disabled={busyStudyId === study.id || study.status === "archived"}
                    data-testid={`study-archive-button-${study.id}`}
                    className="inline-flex min-h-[38px] items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Archive className="h-4 w-4" /> Archiver
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteStudyPermanently(study)}
                    disabled={busyStudyId === study.id}
                    data-testid={`study-delete-permanent-button-${study.id}`}
                    className="inline-flex min-h-[38px] items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="questionnaire-availability-list">
        {loading ? (
          <div className="flex min-h-[180px] items-center justify-center text-slate-400" data-testid="questionnaire-availability-loading">
            <RefreshCw className="mr-2 h-5 w-5 animate-spin" /> Chargement...
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="questionnaire-availability-empty">Aucun questionnaire trouvé</div>
        ) : visibleItems.map((q) => (
          <div key={q.code} className="grid gap-3 border-b border-slate-100 p-4 last:border-b-0 lg:grid-cols-[1fr_220px_170px] lg:items-center" data-testid={`questionnaire-row-${q.code}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-slate-900" data-testid={`questionnaire-name-${q.code}`}>{q.name}</h3>
                {q.isAvailable ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700" data-testid={`questionnaire-status-${q.code}`}><CheckCircle className="h-3.5 w-3.5" /> Disponible</span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600" data-testid={`questionnaire-status-${q.code}`}>Désactivé</span>
                )}
              </div>
              <p className="mt-1 text-xs font-mono text-slate-400" data-testid={`questionnaire-code-${q.code}`}>{q.code}</p>
              <p className="mt-2 line-clamp-2 text-sm text-slate-500" data-testid={`questionnaire-description-${q.code}`}>{q.description}</p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500" data-testid={`questionnaire-tags-${q.code}`}>
                <span>{q.itemCount} items</span>
                <span>~{q.estimatedMinutes || "?"} min</span>
                <span>{q.cancerTypes?.length ? q.cancerTypes.join(", ") : "Générique"}</span>
                <span>{q.targetAudience === "professional" ? "Professionnel" : "Patient"}</span>
              </div>
            </div>
            <div className="text-sm" data-testid={`questionnaire-license-${q.code}`}>
              <p className="font-semibold text-slate-700">{STATUS_LABELS[q.licenseStatus] || q.licenseStatus}</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">{q.usageRestriction}</p>
            </div>
            <button
              type="button"
              onClick={() => toggleAvailability(q)}
              disabled={busyCode === q.code}
              data-testid={`questionnaire-toggle-${q.code}`}
              className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-[background-color,color,transform] active:scale-[0.98] disabled:opacity-60 ${q.isAvailable ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
            >
              {q.isAvailable ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
              {busyCode === q.code ? "Mise à jour..." : q.isAvailable ? "Désactiver" : "Activer"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, testId, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-800",
  };
  return (
    <div className={`rounded-xl border border-slate-200 p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" />
        <p className="text-xs font-bold uppercase">{label}</p>
      </div>
      <p className="mt-2 text-3xl font-black">{value}</p>
    </div>
  );
}